import { onIdTokenChanged, type Auth } from 'firebase/auth';

/**
 * Admin-impersonation session state (the `?becomeToken=` flow — see
 * `becomeUser.ts` and thea-serverless `scripts/become_user.py`).
 *
 * One tiny synchronous module, because its consumers are chokepoints that
 * cannot await: `gaPixel.canFire()` and the event sink's queue/flush gates
 * must answer "is this session impersonating a user?" at call time. An
 * impersonated session's impressions would otherwise feed the suggestion
 * profile's seen-demotion — the exact pollution this flow exists to avoid.
 *
 * Three feeders close the three race windows:
 *  1. ARRIVAL — `becomeUser.ts` marks `impersonating` the moment it sees the
 *     URL param, before the sign-in resolves, so the session's very first
 *     events are already gated.
 *  2. RELOAD — a sessionStorage marker restores the answer synchronously at
 *     next boot (the ID-token claim is truth, but reading it is async).
 *  3. TRUTH — the `impersonator` developer claim on every ID token of the
 *     session (custom-token claims survive token refreshes), read by the
 *     watcher below and by the event sink's own token listener.
 *
 * `unknown` is a real state: a fresh boot with no marker stays `unknown`
 * until the first ID token's claims resolve. GA treats `unknown` as normal
 * (best-effort; the sessionStorage marker makes real leaks a duplicated-tab
 * edge case); the event sink is airtight regardless, because it learns the
 * claims in the same resolution that hands it the ID token it needs to post.
 *
 * TEST ACCOUNTS ride the same rails. An account minted by thea-serverless
 * `scripts/create_test_user.py` carries a PERMANENT `testUser: true` custom
 * claim (set_custom_user_claims — on every token the account ever mints, not
 * per-session like the impersonator developer claim). Playwright, agent, and
 * E2E sessions must stay out of GA and the sink for the same profile-feeding
 * reason, so `updateFromClaims` folds that claim into the same state and the
 * chokepoints ask `isSuppressedSession()` instead of each claim separately.
 * No arrival leg exists for test sessions (there is no URL param); the
 * sessionStorage marker covers reloads, and the sink's claims-with-token
 * coupling covers the first boot exactly as it does for impersonation.
 *
 * QA/AGENT SESSIONS (`?qa=1`) are the third rider, for automation that has no
 * account to carry a claim: the signed-out first visit a QA run checks, an
 * agent-driven browser, a manual spot check. `botDetect.ts` catches honest
 * automation via `navigator.webdriver`, but agent browsers (Playwright MCP,
 * CDP-driven real Chrome) present as fully human — verified against prod
 * 2026-10-31, when such a session posted a real `product_opened` batch. For
 * that traffic the marker must be explicit: any entry URL carrying `?qa=1`
 * flips this leg at MODULE LOAD, which is synchronous and strictly before any
 * chokepoint can run (they all import this module), so even the first
 * page-load events are gated. The param is deliberately NOT stripped from the
 * URL (unlike `becomeToken`, it is a flag, not a credential): a deep link
 * copied mid-session keeps its suppression when pasted into a new tab, where
 * the per-tab sessionStorage marker cannot follow it. Independent of
 * `testSession` on purpose — claims resolution (`updateFromClaims`) must not
 * be able to clear it.
 */

export type ImpersonationStatus = 'unknown' | 'impersonating' | 'normal';

const SESSION_KEY = 'theaImpersonation';
const TEST_SESSION_KEY = 'theaTestSession';
const QA_SESSION_KEY = 'theaQaSession';

/** Entry-URL flag for QA/agent/automation sessions: `?qa=1`. */
export const QA_PARAM = 'qa';

type Listener = () => void;

let status: ImpersonationStatus = 'unknown';
let impersonator: string | null = null;
let testSession = false;
let testSessionLogged = false;
let qaSession = false;
let qaSessionLogged = false;
let watcherInstalled = false;
const listeners = new Set<Listener>();

/**
 * QA arrival leg: `?qa=1` on the entry URL, or the per-tab marker a previous
 * load of this tab wrote. Runs at module load (hoisted call below) so the
 * answer exists before any telemetry chokepoint does. Only the literal '1'
 * counts — mirroring the literal-boolean stance on the testUser claim.
 */
function detectQaArrival(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (window.sessionStorage.getItem(QA_SESSION_KEY)) return true;
  } catch {
    // Storage unavailable (private mode) — the URL check below still works.
  }
  try {
    if (new URLSearchParams(window.location.search).get(QA_PARAM) !== '1') return false;
  } catch {
    return false;
  }
  try {
    window.sessionStorage.setItem(QA_SESSION_KEY, '1');
  } catch {
    // Best-effort; in-memory state still governs this page load.
  }
  return true;
}

function logQaSessionOnce(): void {
  if (qaSessionLogged) return;
  qaSessionLogged = true;
  // The one visible trace, same stance as the test-account line below: no
  // banner (this is an automation affordance, not an impersonation one), but
  // an agent or QA author wondering why nothing reached BQ/GA finds the
  // answer in the console.
  // eslint-disable-next-line no-console
  console.info('[qa] QA/automation session (?qa=1) — GA4 + event sink suppressed');
}

// Reload leg: restore synchronously at module load. sessionStorage is per-tab,
// which is the right scope — an impersonated session deliberately does not
// leak its marker to the user's other tabs.
try {
  if (typeof window !== 'undefined' && window.sessionStorage.getItem(SESSION_KEY)) {
    status = 'impersonating';
  }
  if (typeof window !== 'undefined' && window.sessionStorage.getItem(TEST_SESSION_KEY)) {
    testSession = true;
  }
} catch {
  // Storage unavailable (private mode) — the claim watcher still resolves it.
}

// QA arrival leg, also at module load (see detectQaArrival).
qaSession = detectQaArrival();
if (qaSession) logQaSessionOnce();

function notify(): void {
  listeners.forEach((cb) => {
    try {
      cb();
    } catch {
      // A listener must never break the others (or the auth callback firing us).
    }
  });
}

function writeSessionMarker(on: boolean): void {
  try {
    if (typeof window === 'undefined') return;
    if (on) window.sessionStorage.setItem(SESSION_KEY, '1');
    else window.sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // Best-effort; in-memory state still governs this page load.
  }
}

export function markImpersonating(who?: string): void {
  const changed = status !== 'impersonating' || (who !== undefined && who !== impersonator);
  status = 'impersonating';
  if (who !== undefined) impersonator = who;
  writeSessionMarker(true);
  if (changed) notify();
}

export function markNormal(): void {
  const changed = status !== 'normal' || impersonator !== null;
  status = 'normal';
  impersonator = null;
  writeSessionMarker(false);
  if (changed) notify();
}

export function impersonationStatus(): ImpersonationStatus {
  return status;
}

/** `unknown` answers false — see the module note. */
export function isImpersonating(): boolean {
  return status === 'impersonating';
}

function setTestSession(on: boolean): void {
  if (on === testSession) return;
  testSession = on;
  try {
    if (typeof window !== 'undefined') {
      if (on) window.sessionStorage.setItem(TEST_SESSION_KEY, '1');
      else window.sessionStorage.removeItem(TEST_SESSION_KEY);
    }
  } catch {
    // Best-effort; in-memory state still governs this page load.
  }
  if (on && !testSessionLogged) {
    testSessionLogged = true;
    // The one visible trace (no banner for test sessions — the banner is an
    // impersonation affordance): a spec author wondering why BQ has no rows
    // finds the answer in the console instead of in a hub query.
    // eslint-disable-next-line no-console
    console.info('[testUser] test-account session — GA4 + event sink suppressed');
  }
  notify();
}

/** The account's `testUser: true` custom claim, once resolved. */
export function isTestSession(): boolean {
  return testSession;
}

/** The `?qa=1` arrival leg (QA/agent automation with no account claim). */
export function isQaSession(): boolean {
  return qaSession;
}

/**
 * THE suppression predicate for the telemetry chokepoints (gaPixel.canFire,
 * the event sink's queue/flush/post gates): an impersonated session, a test
 * account, OR a `?qa=1` QA/agent session. All for the same reason — their
 * rows would land in analytics.user_events / GA under a uid whose profile
 * they would poison.
 */
export function isSuppressedSession(): boolean {
  return status === 'impersonating' || testSession || qaSession;
}

/** The actor named in the token's `impersonator` claim, once resolved. */
export function getImpersonator(): string | null {
  return impersonator;
}

export function subscribeImpersonation(cb: Listener): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/**
 * Fold one resolved set of ID-token claims into the state. Shared by the
 * watcher below and the event sink's own `onIdTokenChanged` listener (which
 * reads claims together with the token it caches, so a batch can never be
 * posted under claims we haven't seen).
 */
export function updateFromClaims(claims: Record<string, unknown>): void {
  // Literal boolean only — the claim contract create_test_user.py pins. A
  // truthy string smells like a hand-made account and must not silently
  // vanish from analytics (the BE drop in theaWebLogEvents matches this).
  setTestSession(claims.testUser === true);
  const who = claims.impersonator;
  if (typeof who === 'string' && who) markImpersonating(who);
  else markNormal();
}

/**
 * Truth source: watch the session's ID-token claims. Installed once per app
 * boot (becomeUser.ts). A null user is deliberately NOT folded to `normal`:
 * the initial null before persistence restores, and the gap inside the
 * boot-time `signInWithCustomToken`, must not overwrite the provisional
 * arrival mark — sign-out flows call `markNormal()` explicitly instead.
 */
export function installImpersonationClaimWatcher(auth: Auth): void {
  if (watcherInstalled) return;
  watcherInstalled = true;
  try {
    onIdTokenChanged(auth, (user) => {
      if (!user) return;
      user.getIdTokenResult().then(
        (r) => updateFromClaims(r.claims),
        () => {
          // Claims unreadable — leave the current answer; fail toward
          // whatever the arrival/reload legs established.
        },
      );
    });
  } catch {
    // Auth unavailable (tests/Storybook without config) — arrival and reload
    // legs still work; GA/eventSink fall back to their existing behavior.
  }
}

/** Test-only. */
export function _resetImpersonationForTesting(): void {
  status = 'unknown';
  impersonator = null;
  watcherInstalled = false;
  listeners.clear();
  writeSessionMarker(false);
  testSession = false;
  testSessionLogged = false;
  qaSession = false;
  qaSessionLogged = false;
  try {
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(TEST_SESSION_KEY);
      window.sessionStorage.removeItem(QA_SESSION_KEY);
    }
  } catch {
    // Best-effort, as above.
  }
}

/** Test-only: re-run the module-load QA arrival detection against the
 *  current URL/sessionStorage (jsdom can't re-execute a module body). */
export function _detectQaArrivalForTesting(): void {
  qaSession = detectQaArrival();
  if (qaSession) logQaSessionOnce();
}
